import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Container,
  Snackbar,
} from '@mui/material';

import DashboardHeader from './DashboardHeader';
import CreateSiteDialog from './dialogs/CreateSiteDialog';
import DeleteSiteDialog from './dialogs/DeleteSiteDialog';
import SiteListTab from './tabs/SiteListTab';
import RecordFormDialog from '../../components/dialogs/RecordFormDialog';
import { RecordKind } from '../../components/dialogs/RecordKindSelector';
import QuickRecordFab from '../../components/QuickRecordFab';
import UserManagementTab from './tabs/UserManagementTab';

import useSiteDashboard from './hooks/useSiteDashboard';
import usePermissions from '../../hooks/usePermissions';

import { useDashboardTokens, dashboardTokens } from './tokens';

type TabKey = 'sites' | 'users';

/**
 * 현장 관리 — /projects 진입점.
 *
 *  - 기본 화면 : 좌 현장 테이블 / 우 일정 캘린더 반반 분할 (SiteListTab).
 *  - 유저 관리 : admin 전용 — 탭 UI 는 없고, 프로필 메뉴의 "유저 관리" 버튼이
 *    /projects?tab=users 로 진입시킬 때만 전체 화면으로 노출.
 *
 * 신규 현장/편집/삭제 다이얼로그는 헤더·테이블 양쪽에서 트리거되므로 이 레벨이
 * 소유한다. 루트 Box 는 `height: 100vh; overflowY: auto` 로 지역 스크롤러 확보.
 */
export default function SiteDashboardView() {
  const tokens = useDashboardTokens();
  const dash = useSiteDashboard();
  const { isAdmin, canEdit } = usePermissions();

  // 프로필 메뉴의 "유저 관리" 버튼이 /projects?tab=users 로 진입시킨다 — 탭 UI 는
  // 없으므로 쿼리스트링으로만 화면을 고른다 (전환 UI가 없어 상태로 둘 필요 없음).
  const [searchParams] = useSearchParams();
  const tab: TabKey = searchParams.get('tab') === 'users' ? 'users' : 'sites';

  /**
   * 등록 — 현장에 들어가지 않고 여기서 바로 남긴다(projectId 를 넘기지 않으므로
   * 다이얼로그가 현장을 고르게 한다). 헤더의 업로드 버튼과 우하단 FAB 이 같은
   * 다이얼로그를 공유하며, 어떤 종류인지는 **버튼이 정한다** — 다이얼로그 안에서
   * 다시 고르게 하지 않는다.
   */
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordKind, setRecordKind] = useState<RecordKind>('schedule');
  const openRecord = (kind: RecordKind) => {
    setRecordKind(kind);
    setRecordOpen(true);
  };

  // 캘린더 등록용 전체 현장 목록 — SiteListTab 이 분할 레이아웃 우측 캘린더에 전달.
  const allSites = useMemo(
    () => Array.from(dash.live.sitesByName.values()),
    [dash.live.sitesByName],
  );

  return (
    <Box
      sx={{
        height: '100vh',
        overflowY: 'auto',
        bgcolor: tokens.colors.bg,
        pb: dashboardTokens.space.xl / 8,
      }}
    >
      <DashboardHeader
        searchValue={dash.searchTerm}
        onSearchChange={dash.onSearchChange}
        onCreateClick={dash.openCreate}
      />

      {/* maxWidth=false — 현장목록/캘린더 분할 화면이 xl(1536px) 캡에 갇혀 넓은
          모니터에서 좌우로 못 쓰는 여백이 남는 문제 방지. 헤더와 동일하게 맞춤. */}
      <Container maxWidth={false} sx={{ pt: dashboardTokens.space.md / 8 }}>
        {tab === 'users' && isAdmin ? (
          <UserManagementTab />
        ) : (
          <SiteListTab dash={dash} allSites={allSites} />
        )}
      </Container>

      {/* 등록 — 종류는 버튼이 정하고(kinds 를 하나로 좁혀 선택기를 숨긴다),
          대상 현장만 다이얼로그 안에서 고른다. */}
      <RecordFormDialog
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        initialKind={recordKind}
        kinds={[recordKind]}
        selectableSites={allSites}
        onSaved={() => setRecordOpen(false)}
      />

      {/* 목록을 아래로 훑다가도 위로 올라가지 않고 등록할 수 있게.
          canEdit 없이 띄우면 viewer 가 폼을 다 채우고 저장할 때서야 403 을 본다
          — 같은 화면 헤더의 업로드 버튼은 이미 canEdit 로 가려 두고 있었다. */}
      {tab !== 'users' && canEdit && (
        <QuickRecordFab onClick={() => openRecord('schedule')} label="업무 등록" />
      )}

      {/* 신규 등록 다이얼로그 */}
      <CreateSiteDialog
        open={dash.createOpen}
        onClose={dash.closeCreate}
        onSaved={dash.onCreateSaved}
      />

      {/* 편집 다이얼로그 — CreateSiteDialog 재사용 (initialProject 로 edit 모드 전환) */}
      <CreateSiteDialog
        open={!!dash.editDialogSite}
        initialProject={dash.editDialogSite}
        onClose={dash.closeEdit}
        onSaved={dash.onEditSaved}
      />

      {/* 삭제 다이얼로그 */}
      <DeleteSiteDialog
        site={dash.deleteDialogSite}
        open={!!dash.deleteDialogSite}
        onClose={dash.closeDelete}
        onDeleted={dash.onDeleted}
      />

      {/* Create site snackbar (신규 현장 저장 피드백) */}
      <Snackbar
        open={dash.snackbar.open}
        autoHideDuration={4000}
        onClose={dash.dismissSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={dash.dismissSnackbar}
          severity={dash.snackbar.severity}
          variant="filled"
        >
          {dash.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
